// frontend/src/pages/InsuranceCustomerDetail.jsx
// NEW FEATURE: Insurance Customer Details page, opened from
// SearchInsuranceCustomer.jsx via "View Details".
//
// Reuses the same visual language as CustomerDetail.jsx (cards, spacing,
// colors, buttons, loading spinner, toast notifications) so the experience
// feels identical to the rest of the app. Data source and fields are
// insurance-specific (popular_coupan / insuranceAPI) and the coupon type is
// auto-detected server-side, so only the matching services are shown.
//
// WHY CHANGED: Added Cancel Coupon + Reactivate Coupon + Download Coupon,
// mirroring CustomerDetail.jsx's flow — status badge, cancel modal,
// cancelled banner, per-service CANCELLED display, disabled service
// claiming while cancelled, a Reactivate button that restores everything,
// a live Download Coupon button (Workshop's own stays commented out and
// untouched), and an audit log section covering Cancel/Reactivate. Mark
// Inactive, Preview, and Regenerate are intentionally NOT included here —
// still out of scope.

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, User, Phone, Car, Hash, Ticket, Calendar,
  CheckCircle2, Save, RefreshCw, ShieldCheck, Wrench,
  Clock, MapPin, Tag, MessageCircle, XCircle, AlertTriangle,
  X, History, AlertCircle, RotateCcw, Download,
} from 'lucide-react';
import { insuranceAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

// ── Helpers ──────────────────────────────────────────────────────────────────
const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2.5">
    <Icon className="w-4 h-4 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
    <div className="min-w-0">
      <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">{label}</div>
      <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{value || '—'}</div>
    </div>
  </div>
);

const safeFormat = (value, fmt) => {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return format(d, fmt);
};

// ── Coupon status badge (NEW) — mirrors CustomerDetail.jsx's CouponStatusBadge.
// Insurance currently only ever sets ACTIVE or CANCELLED (Mark Inactive is
// out of scope), but INACTIVE styling is kept here so the badge behaves
// identically to Workshop if that status is ever set directly in the DB.
const CouponStatusBadge = ({ status }) => {
  const s = status || 'ACTIVE';
  const cfg = {
    ACTIVE:    'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700',
    CANCELLED: 'bg-red-100    dark:bg-red-900/30    text-red-800    dark:text-red-300    border-red-300    dark:border-red-700',
    INACTIVE:  'bg-amber-100  dark:bg-amber-900/30  text-amber-800  dark:text-amber-300  border-amber-300  dark:border-amber-700',
  };
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-bold border ${cfg[s] || cfg.ACTIVE}`}>
      {s}
    </span>
  );
};

// ── Cancel confirmation modal (NEW) — mirrors CustomerDetail.jsx's
// CancelModal, trimmed to cancel-only (no inactive mode, per scope).
const CancelCouponModal = ({ onConfirm, onClose, loading }) => {
  const [reason, setReason] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-start justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-red-100 dark:bg-red-900/30">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 dark:text-white">Cancel Coupon</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                This will permanently cancel the insurance coupon.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="p-3 rounded-lg text-sm bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
            ⚠ Once cancelled, all future service claims on this coupon will be blocked.
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Reason <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={3}
              placeholder="Enter reason for cancellation..."
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
            />
          </div>
        </div>

        <div className="flex gap-3 px-6 pb-6">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={loading}
            className="flex-1 px-4 py-2.5 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-60 flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700"
          >
            {loading
              ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : 'Yes, Cancel Coupon'
            }
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Service card ──────────────────────────────────────────────────────────────
// WHY CHANGED: added isDeactivated (coupon-level CANCELLED state, distinct
// from readOnly which is the advisor view-only state). When the coupon is
// cancelled, every not-yet-claimed service shows CANCELLED instead of
// Available and can no longer be selected. Services that were already
// claimed before cancellation keep showing USED — that's a historical
// fact about what was actually claimed, not something cancellation should
// overwrite. Mirrors CustomerDetail.jsx's ServiceCell logic.
const ServiceCard = ({ service, selected, onToggle, isDeactivated, readOnly }) => {
  const isUsed      = service.status === 'USED';
  const isCancelled = isDeactivated && !isUsed;
  const isDisabled  = isUsed || isCancelled || readOnly;

  let cardClass = 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-brand-300';
  if (isCancelled)    cardClass = 'bg-red-50 dark:bg-red-950/40 border-red-300 dark:border-red-800';
  else if (isUsed)    cardClass = 'bg-red-50 dark:bg-red-950/40 border-red-300 dark:border-red-800';
  else if (selected)  cardClass = 'bg-brand-50 dark:bg-brand-900/30 border-brand-400 dark:border-brand-600 shadow-sm';
  else if (readOnly)  cardClass = 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 cursor-default';

  return (
    <div
      className={`relative rounded-xl border p-4 transition-all duration-150 ${cardClass} ${!isDisabled ? 'cursor-pointer' : ''}`}
      onClick={() => !isDisabled && onToggle(service.name)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className={`text-sm font-semibold leading-tight ${(isCancelled || isUsed) ? 'text-red-700 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
            {service.name}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Discount: <span className="font-semibold text-gray-700 dark:text-gray-300">₹{service.discountAmount}</span>
          </div>
        </div>
        {!isUsed && !isCancelled && !readOnly && (
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggle(service.name)}
            onClick={e => e.stopPropagation()}
            className="w-4 h-4 mt-0.5 accent-brand-600 flex-shrink-0"
          />
        )}
      </div>

      <div className="mt-3">
        {isCancelled ? (
          <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-red-600 text-white rounded">CANCELLED</span>
        ) : isUsed ? (
          <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-red-600 text-white rounded">USED</span>
        ) : selected ? (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-brand-700 dark:text-brand-400">
            <CheckCircle2 className="w-3 h-3" /> Selected
          </span>
        ) : (
          <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">Available</span>
        )}
      </div>

      {isUsed && (
        <div className="mt-3 pt-3 border-t border-red-200 dark:border-red-900 space-y-1">
          <div className="text-[11px] text-gray-500 dark:text-gray-400">
            <span className="font-medium text-gray-600 dark:text-gray-300">Claim Date:</span>{' '}
            {safeFormat(service.claimDate, 'dd MMM yyyy') || '—'}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400">
            <span className="font-medium text-gray-600 dark:text-gray-300">Claim Time:</span>{' '}
            {safeFormat(service.claimDate, 'hh:mm a') || '—'}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400">
            <span className="font-medium text-gray-600 dark:text-gray-300">Claimed By:</span>{' '}
            {service.claimedBy || '—'}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400">
            <span className="font-medium text-gray-600 dark:text-gray-300">Branch:</span>{' '}
            {service.branch || '—'}
          </div>
          {service.remarks && (
            <div className="text-[11px] text-gray-500 dark:text-gray-400">
              <span className="font-medium text-gray-600 dark:text-gray-300">Remarks:</span>{' '}
              {service.remarks}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const InsuranceCustomerDetail = () => {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const { isAdvisor } = useAuth();

  const [customer,    setCustomer]    = useState(null);
  const [serviceData, setServiceData] = useState(null);
  const [auditLog,    setAuditLog]    = useState([]);
  const [selected,    setSelected]    = useState(new Set());
  const [remarks,     setRemarks]     = useState('');
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);

  // NEW: Cancel Coupon modal state — mirrors CustomerDetail.jsx's modal /
  // modalLoading pair.
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelLoading,   setCancelLoading]   = useState(false);

  // NEW: loading state for the "Download Coupon" button (Google Drive PDF) —
  // mirrors CustomerDetail.jsx's downloadLoading.
  const [downloadLoading, setDownloadLoading] = useState(false);

  useEffect(() => { loadAll(); }, [id]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const res = await insuranceAPI.getById(id);
      const c   = res.data.data;
      setCustomer(c);
      await Promise.all([
        loadServices(c),
        loadAuditLog(c.coupan_no),
      ]);
    } catch {
      toast.error('Failed to load insurance customer');
      navigate('/search-insurance');
    } finally {
      setLoading(false);
    }
  };

  const loadServices = async (customerData) => {
    try {
      const res = await insuranceAPI.getServices({
        coupanNo: customerData.coupan_no,
        mobile:   customerData.mobile,
      });
      setServiceData(res.data.data);
      setSelected(new Set());
    } catch (err) {
      console.error(err);
      toast.error('Failed to load services');
    }
  };

  // NEW: loads the Cancel audit trail for this coupon — mirrors
  // CustomerDetail.jsx's loadAuditLog. Non-critical: a failure here doesn't
  // block the rest of the page.
  const loadAuditLog = async (coupanNo) => {
    try {
      const res = await insuranceAPI.getAudit({ coupanNo });
      setAuditLog(res.data.data || []);
    } catch {
      // Non-critical
    }
  };

  const toggleService = (name) => {
    if (isAdvisor || isCancelled) return;
    setSelected(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const handleSave = async () => {
    if (isAdvisor || isCancelled) return;
    if (selected.size === 0) {
      toast.error('Please select at least one service to claim');
      return;
    }
    setSaving(true);
    try {
      const res = await insuranceAPI.claimServices({
        coupanNo: customer.coupan_no,
        mobile:   customer.mobile,
        services: [...selected],
        remarks:  remarks.trim() || null,
      });
      const { claimed, alreadyClaimed } = res.data.data;
      if (claimed.length > 0)        toast.success(`${claimed.length} service(s) claimed!`);
      if (alreadyClaimed.length > 0) toast(`${alreadyClaimed.length} already claimed`, { icon: '⚠️' });
      setRemarks('');
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save services');
    } finally {
      setSaving(false);
    }
  };

  // NEW: Cancel Coupon handler — mirrors CustomerDetail.jsx's
  // handleCouponAction (cancel branch only).
  const handleCancelCoupon = async (reason) => {
    if (isAdvisor) return;
    setCancelLoading(true);
    try {
      await insuranceAPI.cancelCoupon({
        coupanNo: customer.coupan_no,
        mobile:   customer.mobile,
        reason,
      });
      toast.success('Insurance coupon cancelled successfully');
      setCancelModalOpen(false);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Cancellation failed');
    } finally {
      setCancelLoading(false);
    }
  };

  // NEW: Reactivate Coupon handler — mirrors CustomerDetail.jsx's
  // handleReactivate (same window.confirm pattern, same audit trail).
  const handleReactivate = async () => {
    if (isAdvisor) return;
    if (!window.confirm('Reactivate this insurance coupon?')) return;
    try {
      await insuranceAPI.reactivateCoupon({
        coupanNo: customer.coupan_no,
        mobile:   customer.mobile,
      });
      toast.success('Insurance coupon reactivated');
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reactivation failed');
    }
  };

  // NEW: downloads the actual Coupon PDF (from Google Drive) for this
  // insurance customer — mirrors CustomerDetail.jsx's handleDownloadCoupon.
  // Independent of Cancel/Reactivate — does not touch that logic.
  const handleDownloadCoupon = async () => {
    setDownloadLoading(true);
    try {
      const res = await insuranceAPI.downloadCoupon({
        coupanNo: customer.coupan_no,
        mobile:   customer.mobile,
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url  = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${customer.coupan_no}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Coupon PDF not found.');
    } finally {
      setDownloadLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!customer) return null;

  const couponStatus = customer.coupon_status || 'ACTIVE';
  const isCancelled  = couponStatus === 'CANCELLED';
  const usedCount  = serviceData?.usedServices      ?? 0;
  const availCount = serviceData?.availableServices ?? 0;
  const total      = serviceData?.totalServices     ?? 0;
  const claimPct   = total ? Math.round((usedCount / total) * 100) : 0;

  return (
    <div className="max-w-6xl mx-auto space-y-5">
      {/* Cancel Coupon modal */}
      {cancelModalOpen && !isAdvisor && (
        <CancelCouponModal
          onConfirm={handleCancelCoupon}
          onClose={() => setCancelModalOpen(false)}
          loading={cancelLoading}
        />
      )}

      {/* Back */}
      <button
        onClick={() => navigate('/search-insurance')}
        className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Search
      </button>

      {isAdvisor && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl border bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-800 text-blue-700 dark:text-blue-400">
          <ShieldCheck className="w-5 h-5 flex-shrink-0" />
          <span className="font-semibold">View-only access. You cannot claim services on this page.</span>
        </div>
      )}

      {/* Cancelled banner (NEW) — mirrors CustomerDetail.jsx's deactivated banner */}
      {isCancelled && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl border bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-800 text-red-700 dark:text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <div>
            <span className="font-semibold">This coupon is CANCELLED.</span>
            {customer.cancel_reason && (
              <span className="ml-2 text-sm">Reason: {customer.cancel_reason}</span>
            )}
            {customer.cancelled_by && (
              <span className="ml-2 text-sm">
                By {customer.cancelled_by}
                {customer.cancelled_at && ` on ${format(new Date(customer.cancelled_at), 'dd MMM yyyy, hh:mm a')}`}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Customer header */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-brand-500 to-brand-700 rounded-2xl flex items-center justify-center text-white text-xl font-bold shadow-lg">
              {customer.customer_name?.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">{customer.customer_name}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 border border-brand-200 dark:border-brand-800">
                  {customer.coupon_type}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">#{customer.coupan_no}</span>
                <CouponStatusBadge status={couponStatus} />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Download Coupon (NEW) — read-only action, visible to both
                branch and advisor roles, same position/style as Workshop's
                button. Workshop's own button stays commented out/untouched;
                this is a live, working version scoped to Insurance only. */}
            { <button
              onClick={handleDownloadCoupon}
              disabled={downloadLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-400 border border-brand-300 dark:border-brand-700 hover:bg-brand-100 dark:hover:bg-brand-900/40 font-semibold rounded-lg transition-colors disabled:opacity-60"
            >
              {downloadLoading
                ? <div className="w-3 h-3 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
                : <Download className="w-3 h-3" />
              }
              Download Coupon
            </button> }

            {/* Cancel / Reactivate Coupon (NEW) — mutually exclusive based on
                coupon_status, hidden for advisors, matching Workshop's
                action-button pattern exactly. */}
            {!isAdvisor && (
              isCancelled ? (
                <button
                  onClick={handleReactivate}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-colors"
                >
                  <RotateCcw className="w-3 h-3" />
                  Reactivate
                </button>
              ) : (
                <button
                  onClick={() => setCancelModalOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-300 dark:border-red-700 hover:bg-red-100 dark:hover:bg-red-900/40 font-semibold rounded-lg transition-colors"
                >
                  <XCircle className="w-3 h-3" />
                  Cancel Coupon
                </button>
              )
            )}

            {/* Stats */}
            <div className="flex items-center gap-3">
              <div className="text-center">
                <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{availCount}</div>
                <div className="text-xs text-gray-500">Available</div>
              </div>
              <div className="w-px h-8 bg-gray-200 dark:bg-gray-700" />
              <div className="text-center">
                <div className="text-xl font-bold text-red-600 dark:text-red-400">{usedCount}</div>
                <div className="text-xs text-gray-500">Used</div>
              </div>
              <div className="w-px h-8 bg-gray-200 dark:bg-gray-700" />
              <div className="text-center">
                <div className="text-xl font-bold text-gray-900 dark:text-white">{total}</div>
                <div className="text-xs text-gray-500">Total</div>
              </div>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-4">
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
            <span>Service utilization</span>
            <span>{claimPct}%</span>
          </div>
          <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-red-500 to-red-600 rounded-full transition-all duration-500"
              style={{ width: `${claimPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* Customer info grid */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
          <User className="w-4 h-4 text-brand-500" />
          Customer Information
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          <InfoRow icon={User}   label="Customer Name"        value={customer.customer_name} />
          <InfoRow icon={Hash}   label="Policy Number"        value={customer.policy_no} />
          <InfoRow icon={Car}    label="Vehicle Regn No"      value={customer.vehicle_regn_no} />
          <InfoRow icon={Car}    label="Vehicle Model"        value={customer.model_name} />
          <InfoRow icon={Calendar} label="Policy Issue Date"    value={customer.policy_issue_date} />
          <InfoRow icon={Calendar} label="Risk Inception Date"  value={customer.risk_inception_date} />
          <InfoRow icon={Calendar} label="Policy Expiry Date"   value={customer.policy_expiry_date} />
          <InfoRow icon={Ticket} label="Coupon Number"        value={customer.coupan_no} />
          <InfoRow icon={Tag}    label="Coupon Type"          value={customer.coupon_type} />
          <InfoRow icon={Phone}  label="Mobile Number"        value={customer.mobile} />
          <InfoRow
            icon={MessageCircle}
            label="WhatsApp Status"
            value={customer.whatsapp_message_sent || customer.first_time_whatsapp_status}
          />
        </div>

        {/* Cancellation details (NEW, only if cancelled) — mirrors
            CustomerDetail.jsx's deactivated-details block. */}
        {isCancelled && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 grid grid-cols-2 sm:grid-cols-3 gap-4">
            <InfoRow icon={XCircle}     label="Coupon Status"  value={couponStatus} />
            {customer.cancelled_by  && <InfoRow icon={User}     label="Cancelled By"  value={customer.cancelled_by} />}
            {customer.cancelled_at  && <InfoRow icon={Clock}    label="Cancelled At"  value={format(new Date(customer.cancelled_at), 'dd MMM yyyy, hh:mm a')} />}
            {customer.cancel_reason && <InfoRow icon={Tag}      label="Cancel Reason" value={customer.cancel_reason} />}
          </div>
        )}
      </div>

      {/* Services grid */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
        <div className="flex flex-wrap items-center justify-between gap-3 p-5 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-brand-500" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              {customer.coupon_type} — Services
            </h2>
            {isCancelled && (
              <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-bold rounded-full border border-red-300 dark:border-red-700">
                CANCELLED
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => loadServices(customer)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
              Refresh
            </button>
            {!isAdvisor && !isCancelled && selected.size > 0 && (
              <span className="text-xs text-brand-600 dark:text-brand-400 font-medium">
                {selected.size} selected
              </span>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-800 flex flex-wrap items-center gap-4 text-xs">
          {!isAdvisor && !isCancelled && (
            <>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-emerald-100 dark:bg-emerald-900/40 border border-emerald-400" />
                <span className="text-gray-500 dark:text-gray-400">Available</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-brand-50 dark:bg-brand-900/30 border border-brand-400" />
                <span className="text-gray-500 dark:text-gray-400">Selected</span>
              </div>
            </>
          )}
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-red-100 dark:bg-red-950/40 border border-red-400" />
            <span className="text-gray-500 dark:text-gray-400">{isCancelled ? 'Cancelled' : 'Used'}</span>
          </div>
        </div>

        {/* Service cards */}
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(serviceData?.services || []).map(service => (
            <ServiceCard
              key={service.name}
              service={service}
              selected={selected.has(service.name)}
              onToggle={toggleService}
              isDeactivated={isCancelled}
              readOnly={isAdvisor}
            />
          ))}
        </div>

        {/* Remarks + Save bar */}
        {!isAdvisor && !isCancelled && selected.size > 0 && (
          <div className="px-5 pb-5 space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                Remarks <span className="text-gray-400">(optional)</span>
              </label>
              <textarea
                value={remarks}
                onChange={e => setRemarks(e.target.value)}
                rows={2}
                placeholder="Enter any remarks for this claim..."
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
              />
            </div>
            <div className="flex items-center justify-between px-1 py-1">
              <div className="flex items-center gap-2 text-sm text-brand-700 dark:text-brand-400">
                <CheckCircle2 className="w-4 h-4" />
                <span className="font-medium">{selected.size} service{selected.size !== 1 ? 's' : ''} selected for claiming</span>
              </div>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm"
              >
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
                Claim Services
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Audit Log (NEW) — mirrors CustomerDetail.jsx's audit log section */}
      {auditLog.length > 0 && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <History className="w-4 h-4 text-brand-500" />
            Coupon Audit History
          </h2>
          <div className="space-y-3">
            {auditLog.map((entry, i) => (
              <div key={i} className="flex items-start gap-3 py-2 border-b border-gray-100 dark:border-gray-800 last:border-0">
                <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${
                  entry.action === 'CANCELLED'   ? 'bg-red-500'   :
                  entry.action === 'INACTIVE'    ? 'bg-amber-500' :
                  entry.action === 'REACTIVATED' ? 'bg-emerald-500' : 'bg-gray-400'
                }`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                      entry.action === 'CANCELLED'   ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'     :
                      entry.action === 'INACTIVE'    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' :
                      'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                    }`}>{entry.action}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      by {entry.performed_by}
                      {entry.branch_name && ` (${entry.branch_name})`}
                    </span>
                  </div>
                  {entry.reason && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{entry.reason}</p>
                  )}
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                    {entry.performed_at ? format(new Date(entry.performed_at), 'dd MMM yyyy, hh:mm a') : '—'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default InsuranceCustomerDetail;